<template>
  <div class="admin-api-log-manage">
    <h2>API 日志管理</h2>
    <el-form :inline="true" :model="searchForm" @submit.native.prevent="onSearch" :label-position="isMobile ? 'top' : 'right'" style="margin-bottom: 16px;">
      <el-form-item label="用户">
        <div class="user-search-container">
          <el-select v-model="userSearchType" placeholder="搜索类型" size="small" style="width: 90px; margin-right: 5px;" @change="onUserSearchTypeChange">
            <el-option label="用户名" value="username"></el-option>
            <el-option label="用户ID" value="user_id"></el-option>
            <el-option label="自动" value="all"></el-option>
          </el-select>
          <el-input v-model="searchForm.userSearch" :placeholder="userSearchType === 'username' ? '输入用户名' : userSearchType === 'user_id' ? '输入用户ID' : '用户名或ID'" style="width: 120px" @keyup.enter.native="onSearch" size="small" />
        </div>
      </el-form-item>
      <el-form-item label="API Key">
        <el-input v-model="searchForm.api_key" placeholder="可选，API Key" style="width: 180px" @keyup.enter.native="onSearch" />
      </el-form-item>
      <el-form-item label="接口">
        <el-input v-model="searchForm.endpoint" placeholder="可选，接口名" style="width: 120px" @keyup.enter.native="onSearch" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="全部" clearable style="width: 100px">
          <el-option label="全部" value="" />
          <el-option label="成功 (200)" value="200" />
          <el-option label="未授权 (401)" value="401" />
          <el-option label="禁止 (403)" value="403" />
          <el-option label="限流 (429)" value="429" />
          <el-option label="错误 (500)" value="500" />
        </el-select>
      </el-form-item>
      <el-form-item label="IP地址">
        <el-input v-model="searchForm.ip" placeholder="可选，IP地址" style="width: 140px" @keyup.enter.native="onSearch" />
      </el-form-item>
      <el-form-item label="日期范围">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          style="width: 240px"
        ></el-date-picker>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="onSearch">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
        <el-button type="success" @click="exportCSV">导出CSV</el-button>
        <el-button type="danger" @click="showCleanupDialog">清理日志</el-button>
      </el-form-item>
    </el-form>
    
    <!-- 日志统计信息 -->
    <el-card v-if="logStats" class="stats-card" style="margin-bottom: 16px;">
      <div slot="header">
        <span>日志统计</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="fetchLogStats">刷新</el-button>
      </div>
      <el-row :gutter="20">
        <el-col :span="4">
          <div class="stat-item">
            <div class="stat-value">{{ logStats.total || 0 }}</div>
            <div class="stat-label">总记录数</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-item">
            <div class="stat-value">{{ logStats.last_1_day || 0 }}</div>
            <div class="stat-label">最近1天</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-item">
            <div class="stat-value">{{ logStats.last_7_days || 0 }}</div>
            <div class="stat-label">最近7天</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-item">
            <div class="stat-value">{{ logStats.last_30_days || 0 }}</div>
            <div class="stat-label">最近30天</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-item">
            <div class="stat-value">{{ logStats.last_90_days || 0 }}</div>
            <div class="stat-label">最近90天</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-item">
            <div class="stat-value text-warning">{{ logStats.older_than_90_days || 0 }}</div>
            <div class="stat-label">90天前</div>
          </div>
        </el-col>
      </el-row>
    </el-card>
    <!-- PC端表格 -->
    <el-table v-loading="loading" :data="list" style="width: 100%" :class="{'mobile-table': isMobile}" size="small">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column label="用户" min-width="120">
        <template slot-scope="scope">
          <span>{{ formatUser(scope.row) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="API Key" min-width="200">
        <template slot-scope="scope">
          <el-tooltip :content="scope.row.api_key" placement="top" :disabled="!scope.row.api_key">
            <span class="api-key-display">
              {{ scope.row.api_key ? formatApiKey(scope.row.api_key) : '无API Key' }}
              <el-button v-if="scope.row.api_key" type="text" size="mini" @click="copyText(scope.row.api_key)">
                <i class="el-icon-document-copy"></i>
              </el-button>
            </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="endpoint" label="接口" width="120" />
      <el-table-column label="参数" min-width="180">
        <template slot-scope="scope">
          <el-tooltip 
            v-if="scope.row.params && scope.row.params.length > 50" 
            :content="scope.row.params" 
            placement="top"
          >
            <div class="params-preview">
              {{ formatParams(scope.row.params) }}
              <el-button type="text" size="mini" @click="showParamsDetail(scope.row.params)">
                <i class="el-icon-view"></i>
              </el-button>
            </div>
          </el-tooltip>
          <span v-else class="params-text">{{ scope.row.params || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="IP" width="140">
        <template slot-scope="scope">
          <div class="ip-display">
            {{ scope.row.ip || 'N/A' }}
            <el-button 
              v-if="scope.row.ip && scope.row.ip !== '127.0.0.1'" 
              type="text" 
              size="mini" 
              @click="searchByIp(scope.row.ip)"
            >
              <i class="el-icon-search"></i>
            </el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="80">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)">{{ scope.row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="message" label="消息" width="120" />
      <el-table-column prop="create_time" label="时间" width="180" />
    </el-table>
    <!-- 移动端卡片列表 -->
    <div v-if="isMobile" class="mobile-card-list">
      <div v-for="item in list" :key="item.id || item.api_key" class="mobile-card">
        <div class="mobile-card-title">
          <span class="mobile-card-title-text">API日志 #{{ item.id }}</span>
          <el-tag :type="getStatusType(item.status)" size="mini">{{ item.status }}</el-tag>
        </div>
        
        <div class="mobile-card-row">
          <span class="mobile-card-label">用户：</span>
          <span>{{ formatUser(item) }}</span>
        </div>
        
        <div class="mobile-card-row">
          <span class="mobile-card-label">API Key：</span>
          <span class="api-key-mobile">
            {{ item.api_key ? formatApiKey(item.api_key) : '无API Key' }}
            <el-button v-if="item.api_key" type="text" size="mini" @click="copyText(item.api_key)">
              <i class="el-icon-document-copy"></i>
            </el-button>
          </span>
        </div>
        
        <div class="mobile-card-row">
          <span class="mobile-card-label">接口：</span>
          {{ item.endpoint }}
        </div>
        
        <div class="mobile-card-row">
          <span class="mobile-card-label">参数：</span>
          <span class="mobile-card-params">{{ item.params }}</span>
        </div>
        
        <div class="mobile-card-row">
          <span class="mobile-card-label">IP：</span>
          {{ item.ip || 'N/A' }}
        </div>
        
        <div class="mobile-card-row">
          <span class="mobile-card-label">消息：</span>
          {{ item.message || 'N/A' }}
        </div>
        
        <div class="mobile-card-row">
          <span class="mobile-card-label">时间：</span>
          {{ item.create_time }}
        </div>
      </div>
      <div v-if="!list.length && !loading" class="no-data-tip">暂无数据</div>
    </div>
    <el-pagination
      style="margin: 16px 0; text-align: right"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :current-page="searchForm.page"
      :page-size="searchForm.pageSize"
      :total="total"
      @current-change="onPageChange"
      @size-change="onSizeChange"
      :page-sizes="[10, 20, 50, 100]"
    />
    
    <!-- 参数详情对话框 -->
    <el-dialog
      title="参数详情"
      :visible.sync="paramsDetailVisible"
      width="80%"
      class="params-detail-dialog"
    >
      <pre>{{ currentParams }}</pre>
      <span slot="footer" class="dialog-footer">
        <el-button @click="paramsDetailVisible = false">关闭</el-button>
        <el-button type="primary" @click="copyText(currentParams)">复制</el-button>
      </span>
    </el-dialog>
    
    <!-- 日志清理对话框 -->
    <el-dialog title="日志清理" :visible.sync="cleanupDialogVisible" width="500px">
      <div style="margin-bottom: 20px;">
        <el-alert
          title="警告：此操作不可逆！"
          type="warning"
          description="清理操作将永久删除指定时间范围内的日志记录，请谨慎操作。"
          show-icon
          :closable="false">
        </el-alert>
      </div>
      
      <el-form :model="cleanupForm" label-width="120px">
        <el-form-item label="清理范围">
          <el-select v-model="cleanupForm.days" placeholder="请选择清理范围">
            <el-option label="清理7天前的日志" :value="7"></el-option>
            <el-option label="清理30天前的日志" :value="30"></el-option>
            <el-option label="清理90天前的日志" :value="90"></el-option>
            <el-option label="清理180天前的日志" :value="180"></el-option>
            <el-option label="清理365天前的日志" :value="365"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="确认文本">
          <el-input 
            v-model="cleanupForm.confirmText" 
            placeholder="请输入：确认清理日志"
            @keyup.enter.native="confirmCleanup">
          </el-input>
          <div style="font-size: 12px; color: #999; margin-top: 5px;">
            为了安全，请输入：<strong>确认清理日志</strong>
          </div>
        </el-form-item>
        
        <div v-if="cleanupForm.days && logStats" style="background: #f5f7fa; padding: 15px; border-radius: 4px; margin-bottom: 20px;">
          <div style="font-weight: bold; margin-bottom: 10px;">预计清理数量：</div>
          <div v-if="cleanupForm.days === 7">
            将清理 <span style="color: #e6a23c; font-weight: bold;">{{ logStats.total - logStats.last_7_days }}</span> 条记录
          </div>
          <div v-else-if="cleanupForm.days === 30">
            将清理 <span style="color: #e6a23c; font-weight: bold;">{{ logStats.total - logStats.last_30_days }}</span> 条记录
          </div>
          <div v-else-if="cleanupForm.days === 90">
            将清理 <span style="color: #e6a23c; font-weight: bold;">{{ logStats.total - logStats.last_90_days }}</span> 条记录
          </div>
          <div v-else>
            将清理 <span style="color: #e6a23c; font-weight: bold;">{{ getEstimatedCleanupCount() }}</span> 条记录
          </div>
        </div>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="cleanupDialogVisible = false">取消</el-button>
        <el-button 
          type="danger" 
          @click="confirmCleanup" 
          :loading="cleanupLoading"
          :disabled="!cleanupForm.days || cleanupForm.confirmText !== '确认清理日志'">
          确认清理
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'AdminApiLogManage',
  data() {
    return {
      searchForm: { userSearch: '', api_key: '', endpoint: '', status: '', ip: '', page: 1, pageSize: 20 },
      dateRange: [],
      list: [],
      total: 0,
      loading: false,
      isMobile: false,
      paramsDetailVisible: false,
      currentParams: '',
      userSearchType: 'all', // 新增：用户搜索类型
      // 日志清理相关
      cleanupDialogVisible: false,
      cleanupLoading: false,
      cleanupForm: {
        days: null,
        confirmText: ''
      },
      logStats: null // 日志统计数据
    }
  },
  created() { this.onSearch(); this.checkMobile(); this.fetchLogStats(); window.addEventListener('resize', this.checkMobile); },
  destroyed() { window.removeEventListener('resize', this.checkMobile); },
  methods: {
    checkMobile() { this.isMobile = window.innerWidth < 768; },
    onSearch() {
      this.searchForm.page = 1;
      this.getList();
    },
    getList() {
      this.loading = true;
      const params = { ...this.searchForm };
      
      // 处理用户搜索 - 根据搜索类型处理
      if (params.userSearch) {
        if (this.userSearchType === 'username') {
          // 仅按用户名搜索
          params.username = params.userSearch;
        } else if (this.userSearchType === 'user_id') {
          // 仅按用户ID搜索
          params.user_id = params.userSearch;
        } else {
          // 智能判断是ID还是用户名
          if (!isNaN(params.userSearch) && /^\d+$/.test(params.userSearch)) {
            params.user_id = params.userSearch; // 如果是纯数字，作为user_id查询
          } else {
            params.username = params.userSearch; // 否则作为username查询
          }
        }
        delete params.userSearch; // 删除中间变量
      }
      
      // 处理日期范围
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0];
        params.endTime = this.dateRange[1];
      }

      this.$axios.get('/admin/api_log/list', { params })
        .then(res => {
          if (res && res.code === 200) {
            this.list = res.data || [];
            this.total = res.total || 0;
          } else {
            this.$message.error(res?.message || '获取数据失败');
            this.list = [];
            this.total = 0;
          }
        })
        .catch(err => {
          console.error('获取API日志列表失败:', err);
          this.$message.error('获取API日志列表失败: ' + (err.message || '未知错误'));
          this.list = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onPageChange(page) { 
      this.searchForm.page = page; 
      this.getList(); 
    },
    
    onSizeChange(size) { 
      this.searchForm.pageSize = size; 
      this.searchForm.page = 1; 
      this.getList(); 
    },
    
    searchByIp(ip) {
      this.searchForm.ip = ip;
      this.onSearch();
    },
    
    resetSearch() {
      this.searchForm = { userSearch: '', api_key: '', endpoint: '', status: '', ip: '', page: 1, pageSize: 20 };
      this.dateRange = [];
      this.userSearchType = 'all';
      this.onSearch();
    },
    
    formatApiKey(key) {
      if (!key) return '';
      const len = key.length;
      if (len <= 4) return key;
      return key.substring(0, 4) + '...' + key.substring(len - 4);
    },
    
    copyText(text) {
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      this.$message.success('API Key 已复制到剪贴板');
    },
    
    formatUser(row) {
      if (row.username) {
        return `${row.username}${row.user_id ? ` (ID: ${row.user_id})` : ''}`;
      } else if (row.user_id) {
        return `ID: ${row.user_id}`;
      } else {
        return '未知用户';
      }
    },
    exportCSV() {
      const params = { ...this.searchForm };
      
      // 处理用户搜索 - 根据搜索类型处理
      if (params.userSearch) {
        if (this.userSearchType === 'username') {
          // 仅按用户名搜索
          params.username = params.userSearch;
        } else if (this.userSearchType === 'user_id') {
          // 仅按用户ID搜索
          params.user_id = params.userSearch;
        } else {
          // 智能判断是ID还是用户名
          if (!isNaN(params.userSearch) && /^\d+$/.test(params.userSearch)) {
            params.user_id = params.userSearch; // 如果是纯数字，作为user_id查询
          } else {
            params.username = params.userSearch; // 否则作为username查询
          }
        }
        delete params.userSearch; // 删除中间变量
      }
      
      // 处理日期范围
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0];
        params.endTime = this.dateRange[1];
      }
      
      // 构建查询字符串
      const queryString = Object.keys(params)
        .filter(key => params[key] !== '' && params[key] !== null && params[key] !== undefined)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
      
      // 下载文件
      window.open(`/api/admin/api_log/download?${queryString}`);
    },
    getStatusType(status) {
      if (status === 200 || status === '200') return 'success';
      if (status === 401 || status === '401') return 'warning';
      if (status === 403 || status === '403') return 'danger';
      if (status === 429 || status === '429') return 'info';
      if (status === 500 || status === '500') return 'danger';
      return ''; // Default for unknown statuses
    },
    formatParams(params) {
      if (!params) return '-';
      try {
        const parsed = JSON.parse(params);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return params;
      }
    },
    showParamsDetail(params) {
      this.currentParams = this.formatParams(params);
      this.paramsDetailVisible = true;
    },
    onUserSearchTypeChange() {
      this.searchForm.userSearch = ''; // 清空用户名或ID输入框
      this.onSearch();
    },
    
    // 获取日志统计信息
    async fetchLogStats() {
      try {
        const res = await this.$axios.get('/admin/api_log/stats');
        if (res && res.code === 200) {
          this.logStats = res.data;
        }
      } catch (err) {
        console.error('获取日志统计失败:', err);
      }
    },
    
    // 显示清理对话框
    showCleanupDialog() {
      this.cleanupDialogVisible = true;
      this.cleanupForm = {
        days: null,
        confirmText: ''
      };
      // 刷新统计数据
      this.fetchLogStats();
    },
    
    // 确认清理日志
    async confirmCleanup() {
      if (!this.cleanupForm.days) {
        this.$message.error('请选择清理范围');
        return;
      }
      
      if (this.cleanupForm.confirmText !== '确认清理日志') {
        this.$message.error('请输入正确的确认文本');
        return;
      }
      
      this.cleanupLoading = true;
      
      try {
        const res = await this.$axios.post('/admin/api_log/cleanup', {
          days: this.cleanupForm.days,
          confirmText: this.cleanupForm.confirmText
        });
        
        if (res && res.code === 200) {
          this.$message.success(res.message || '日志清理成功');
          this.cleanupDialogVisible = false;
          // 刷新列表和统计
          this.onSearch();
          this.fetchLogStats();
        } else {
          this.$message.error(res?.message || '日志清理失败');
        }
      } catch (err) {
        console.error('日志清理失败:', err);
        this.$message.error('日志清理失败: ' + (err.response?.data?.message || err.message || '未知错误'));
      } finally {
        this.cleanupLoading = false;
      }
    },
    
    // 获取预计清理数量
    getEstimatedCleanupCount() {
      if (!this.logStats) return 0;
      
      // 对于180天和365天，我们估算一个数量
      if (this.cleanupForm.days === 180) {
        // 假设180天前的数据大约是90天前数据的一半
        return Math.max(0, this.logStats.total - Math.floor(this.logStats.last_90_days * 0.5));
      } else if (this.cleanupForm.days === 365) {
        // 假设365天前的数据很少
        return Math.max(0, this.logStats.total - Math.floor(this.logStats.last_90_days * 0.1));
      }
      
      return 0;
    }
  }
}
</script>

<style>
/* 全局样式，不使用scoped */
.params-detail-dialog .el-dialog__body {
  white-space: pre-wrap;
  font-family: monospace;
  max-height: 400px;
  overflow-y: auto;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
}
</style>

<style scoped>
.admin-api-log-manage {
  max-width: 1200px;
  margin: 32px auto;
  background: #fff;
  padding: 24px;
  border-radius: 8px;
}

.user-search-container {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .admin-api-log-manage {
    padding: 6px;
    margin: 8px auto;
  }
  h2 {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }
  .el-form {
    flex-direction: column !important;
    align-items: stretch !important;
    display: flex !important;
  }
  .el-form-item {
    margin-bottom: 8px;
    width: 100%;
    display: flex;
    flex-direction: column;
  }
  .el-input,
  .el-select,
  .el-button {
    width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box;
  }
  .el-table {
    min-width: 800px;
  }
  .el-table,
  .el-table__body-wrapper {
    overflow-x: auto !important;
    display: block;
    width: 100%;
  }
  .mobile-card-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0 0 12px 0;
  }
  .mobile-card {
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 14px 12px 10px 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .mobile-card-title {
    font-weight: 600;
    font-size: 15px;
    color: #333;
    margin-bottom: 2px;
    word-break: break-all;
  }
  .mobile-card-title-text {
    margin-right: 8px;
  }
  .mobile-card-row {
    font-size: 13px;
    margin-bottom: 2px;
    word-break: break-all;
  }
  .mobile-card-label {
    font-weight: 500;
    color: #606266;
    margin-right: 4px;
  }
  .api-key-mobile {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .mobile-card-params {
    word-break: break-all;
  }
  .unknown-user {
    color: #909399;
    font-size: 0.9em;
  }
  .api-key-display {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .params-preview {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
  }
  .params-text {
    color: #606266;
    font-size: 0.9em;
  }
  .ip-display {
    display: flex;
  }
  
  /* 统计卡片样式 */
  .stats-card .stat-item {
    text-align: center;
    padding: 10px;
  }
  
  .stats-card .stat-value {
    font-size: 24px;
    font-weight: bold;
    color: #409EFF;
    margin-bottom: 5px;
  }
  
  .stats-card .stat-value.text-warning {
    color: #E6A23C;
  }
  
  .stats-card .stat-label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 0;
    align-items: center;
    gap: 4px;
  }
}
@media (min-width: 768px) {
  .mobile-card-list { display: none; }
}
</style>